<template>
  <modal name="station-select-modal" :height="'auto'" :width="'40%'" :scrollable="true">
    <div class="flex items-center justify-between p-4 border-b">
      <h4 class="text-base font-semibold">İstasyon Seç ve Yönlendir</h4>
      <button @click="$modal.hide('station-select-modal')">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div class="p-4">
      <div v-if="!hasLocation" class="mb-3 p-2 bg-yellow-50 border border-yellow-300 rounded text-xs text-yellow-800">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        Talepte konum bilgisi bulunmadığı için istasyonlar mesafe sıralaması olmadan listelenmiştir.
      </div>
      <input v-model="stationSearch" type="text" placeholder="İstasyon ara..."
        class="w-full p-2 border rounded mb-3 text-sm dark:bg-gray-700 dark:text-white" />
      <div class="max-h-64 overflow-y-auto space-y-2">
        <div v-for="(station, index) in filteredStations" :key="station.id"
          class="flex items-center justify-between p-3 rounded cursor-pointer hover:bg-blue-50 dark:hover:bg-gray-700"
          :class="[
            selectedStationId === station.id ? 'bg-blue-100 dark:bg-gray-600 border-blue-500 border-2' : 'border',
            index === 0 && hasLocation && station.distance != null && !stationSearch ? 'border-green-500 border-2 bg-green-50 dark:bg-green-900' : ''
          ]"
          @click="selectedStationId = station.id">
          <div>
            <div class="flex items-center space-x-2">
              <span class="font-semibold text-sm">{{ station.name }}</span>
              <span v-if="index === 0 && hasLocation && station.distance != null && !stationSearch" class="px-1.5 py-0.5 text-white text-xxs rounded" style="background-color: #16a34a;">En Yakın</span>
            </div>
            <div class="text-xxs text-gray-500">{{ station.city }} / {{ station.district }} - {{ station.contactPhone }}</div>
            <div class="text-xxs text-gray-400">{{ station.fuelTypes }}</div>
            <div class="flex items-center space-x-3 mt-1">
              <span class="text-xxs text-blue-600 font-medium">
                <i class="fas fa-road mr-1"></i>{{ hasLocation && station.distance != null ? station.distance + ' km' : '-' }}
              </span>
              <span v-if="hasLocation && station.estimatedMinutes != null" class="text-xxs text-gray-600 font-medium">
                <i class="fas fa-clock mr-1"></i>{{ station.estimatedMinutes }} dk
              </span>
            </div>
          </div>
          <i v-if="selectedStationId === station.id" class="fas fa-check-circle text-blue-500 text-lg"></i>
        </div>
        <div v-if="filteredStations.length === 0" class="text-center text-gray-400 py-4">
          İstasyon bulunamadı
        </div>
      </div>
    </div>
    <div class="p-4 border-t flex justify-end">
      <asyncBtn icon="fas fa-paper-plane" text="Yönlendir ve SMS Gönder" class="w-56 btn" :loadState="loading" @click="directToStation" />
    </div>
  </modal>
</template>

<script>
import asyncBtn from "@/components/general/asyncBtn.vue";
import axios from "axios";
import { fuel } from "@/networking/urlmanager";

export default {
  name: "station-select-modal",
  components: { asyncBtn },
  props: {
    stations: { type: Array, default: () => [] },
    requestId: { type: Number, default: null },
    hasLocation: { type: Boolean, default: true },
    request: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      selectedStationId: null,
      stationSearch: "",
    };
  },
  computed: {
    filteredStations() {
      let list = [...this.stations];
      if (this.stationSearch) {
        const q = this.stationSearch.toLowerCase();
        list = list.filter(s =>
          (s.name || "").toLowerCase().includes(q) ||
          (s.city || "").toLowerCase().includes(q) ||
          (s.district || "").toLowerCase().includes(q)
        );
      }
      if (this.hasLocation) {
        list.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
      }
      return list;
    },
  },
  methods: {
    formatItemsSummary() {
      const items = this.request?.items || [];
      if (items.length === 0) return "";
      return items.map(it => {
        const fuelType = it.fuelType || "—";
        if (it.isFillTank) return `TAM DOLUM ${fuelType}`;
        const liters = it.approvedLiters != null ? it.approvedLiters : it.requestedLiters;
        return `${liters} litre ${fuelType}`;
      }).join(" + ");
    },
    directToStation() {
      if (!this.selectedStationId) {
        this.swalBox("warning", "Uyarı", "Lütfen bir istasyon seçiniz.", false);
        return;
      }

      const station = this.stations.find(s => s.id === this.selectedStationId);
      const stationName = station?.name || "İstasyon";
      const plate = this.request?.vehiclePlate || "";
      const itemsText = this.formatItemsSummary();

      const html = `
        <div style="text-align:left;font-size:13px;">
          <b>${stationName}</b> istasyonuna SMS gönderilecek.<br/><br/>
          ${plate ? `<b>${plate}</b> plakalı araç için` : ""}
          ${itemsText ? `<br/><b>${itemsText}</b>` : ""}
          ${itemsText ? "<br/>" : ""}
          yönlendirme bildirimi gidecek.<br/><br/>
          <span style="color:#dc2626;">Onaylıyor musunuz?</span>
        </div>
      `;

      this.$swal.fire({
        title: "İstasyon Yönlendirme Onayı",
        html,
        showCancelButton: true,
        confirmButtonText: "Onaylıyorum",
        cancelButtonText: "İptal",
        confirmButtonColor: "#16a34a",
        cancelButtonColor: "#6b7280",
      }).then((result) => {
        if (!result.isConfirmed) return;
        this.sendDirectRequest();
      });
    },
    sendDirectRequest() {
      this.loading = true;
      axios.post(fuel.requestDirectStation, {
        requestId: this.requestId,
        stationId: this.selectedStationId,
      }, {
        headers: { Authorization: "Bareer " + this.$store.state.userData.token },
      })
        .then((res) => {
          const smsSent = res.data?.data?.smsSent;
          this.swalBox("success", "Yönlendirildi", smsSent ? "SMS istasyona gönderildi." : "Yönlendirme yapıldı (SMS gönderilemedi).", false);
          this.$emit("refresh");
          this.$modal.hide("station-select-modal");
        })
        .catch((err) => { this.errorBox(err.response); })
        .finally(() => { this.loading = false; });
    },
  },
};
</script>
